import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

import { useFormatValue } from "../../../hooks/useFormatValue";
import { translate } from "../../../hooks/translate";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { dateStrInAppTz } from "../../../utils/appTime";
import { limitWords } from "../../../utils/textDisplay";

import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Input from "../../../components/BaseUI/Input";
import Button from "../../../components/BaseUI/Button";
import Label from "../../../components/BaseUI/Label";
import Lozenge from "../../../components/BaseUI/Lozenge";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Checkbox from "../../../components/BaseUI/Checkbox";
import Select from "../../../components/BaseUI/Select";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
// Shared price display: follows the system USD/TL toggle in the header
import ProductPrice from "../../../components/CustomUI/ProductPrice";
import { useExchangeRateOptional } from "../../../contexts/exchangeRate";

import {
  FiShoppingBag,
  FiCalendar,
  FiDollarSign,
  FiCreditCard,
  FiServer,
  FiTrash2,
  FiPackage,
  FiCheckCircle,
  FiFileText,
  FiPercent,
  FiUser,
} from "react-icons/fi";
import { RiFileTransferLine } from "react-icons/ri";

import {
  clampPct,
  calculateListSubtotal,
  calculateFinalTotal,
  getLineDiscountInfo,
  roundMoney,
} from "../../../utils/saleDiscountCalc";
import {
  DEFAULT_SALES_TERMS,
  DEFAULT_TESLIMAT_YONTEMI,
  DEFAULT_OPSIYON_SURESI,
  DEFAULT_EK_BILGI_NOTU,
  TESLIMAT_YONTEMI_OPTIONS,
  resolveOrderSalesTerms,
} from "../../../constants/offerPdfDefaults";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";
const KDV_RATE = 0.2;

const paymentMethodLabels = {
  cash: "Nakit",
  credit_card: "Kredi Kartı",
  bank_transfer: "Banka Transferi",
  document: "Senet/Belge",
};

const orderHeaderKindSelectOptions = [
  { value: "percent", label: "Yüzde (%)" },
  { value: "fixed", label: "Sabit tutar (USD)" },
];

const inferDiscountScopeFromOrder = (order) => {
  if (
    order.discountScope === "order_total" ||
    order.discountScope === "per_line"
  ) {
    return order.discountScope;
  }
  const hasLine = (order.models || []).some(
    (m) => Number(m.discountPercent) > 0,
  );
  const hasHeader =
    Number(order.orderDiscountPercent) > 0 ||
    Number(order.orderDiscountFixed) > 0;
  if (hasHeader && !hasLine) return "order_total";
  return "per_line";
};

const defaultTerminTarihi = (fromDateStr) => {
  const base = fromDateStr ? new Date(fromDateStr) : new Date();
  if (Number.isNaN(base.getTime())) {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return dateStrInAppTz(d);
  }
  base.setDate(base.getDate() + 7);
  return dateStrInAppTz(base);
};

const mapOrderModelsToSelected = (order) =>
  (order?.models || []).map((m) => ({
    modelId:
      m.modelId && typeof m.modelId === "object"
        ? (m.modelId._id ?? null)
        : (m.modelId ?? null),
    code: m.modelId?.code || m.code || "",
    name: m.modelId?.name || m.name || "",
    count: m.count || 1,
    price: m.modelId?.price || m.price || 0,
    discountPercent: Number(m.discountPercent) || 0,
    terminType: m.terminType === "date" ? "date" : "stock",
    terminDate:
      m.terminType === "date" && m.terminDate
        ? dateStrInAppTz(new Date(m.terminDate))
        : "",
  }));

const mapModelTerminForSave = (model, fallbackDate) => ({
  terminType: model.terminType === "date" ? "date" : "stock",
  ...(model.terminType === "date"
    ? { terminDate: model.terminDate || fallbackDate }
    : {}),
});

const buildOrderFormStateFromOrder = (order, { useToday = false } = {}) => {
  const orderDate = useToday
    ? dateStrInAppTz(new Date())
    : order.orderDate
      ? dateStrInAppTz(new Date(order.orderDate))
      : dateStrInAppTz(new Date());

  return {
    orderName: useToday ? "" : order.orderName || "",
    companyId:
      order.companyId && typeof order.companyId === "object"
        ? (order.companyId._id ?? null)
        : (order.companyId ?? null),
    sequence: "",
    orderDate,
    terminTarihi: useToday
      ? defaultTerminTarihi(orderDate)
      : order.terminTarihi
        ? dateStrInAppTz(new Date(order.terminTarihi))
        : defaultTerminTarihi(orderDate),
    paymentMethod: order.paymentMethod || "cash",
    notes: order.notes || "",
    yetkiliKisi: order.yetkiliKisi || order.companyId?.yetkiliKisi || "",
    pdfDescription: resolveOrderSalesTerms(order),
    bankAccountIds: Array.isArray(order.bankAccountIds)
      ? order.bankAccountIds.map((id) => String(id))
      : order.bankAccountId
        ? [String(order.bankAccountId)]
        : [],
    teslimatYontemi:
      order.teslimatYontemi ||
      order.pdfContent?.teslimatYontemi ||
      DEFAULT_TESLIMAT_YONTEMI,
    opsiyonSuresi:
      order.opsiyonSuresi ||
      order.pdfContent?.opsiyonSuresi ||
      DEFAULT_OPSIYON_SURESI,
    ekBilgiNotu:
      order.ekBilgiNotu ||
      order.pdfContent?.ekBilgiNotu ||
      DEFAULT_EK_BILGI_NOTU,
    discountScope: inferDiscountScopeFromOrder(order),
    orderHeaderKind:
      Number(order.orderDiscountFixed) > 0 &&
      !(Number(order.orderDiscountPercent) > 0)
        ? "fixed"
        : "percent",
    orderDiscountPercent: Number(order.orderDiscountPercent) || 0,
    orderDiscountFixed: Number(order.orderDiscountFixed) || 0,
    exchangeRate:
      order.exchangeRate !== undefined && order.exchangeRate !== null
        ? String(order.exchangeRate)
        : "",
  };
};

const CreateOrder = () => {
  const [models, setModels] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingOrder, setFetchingOrder] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [bulkDiscountModelIds, setBulkDiscountModelIds] = useState([]);
  const [bulkDiscountPercent, setBulkDiscountPercent] = useState("");

  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const recreateFromId = searchParams.get("recreateFrom");
  const isEditMode = !!orderId;
  const isRecreateMode = !isEditMode && !!recreateFromId;

  const format = useFormatValue;
  const navigate = useNavigate();
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/orders",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [orderData, setOrderData] = useState(() => ({
    orderName: "",
    companyId: null,
    sequence: "",
    orderDate: dateStrInAppTz(new Date()),
    terminTarihi: defaultTerminTarihi(dateStrInAppTz(new Date())),
    paymentMethod: "cash",
    notes: "",
    yetkiliKisi: "",
    pdfDescription: DEFAULT_SALES_TERMS,
    bankAccountIds: [],
    teslimatYontemi: DEFAULT_TESLIMAT_YONTEMI,
    opsiyonSuresi: DEFAULT_OPSIYON_SURESI,
    ekBilgiNotu: DEFAULT_EK_BILGI_NOTU,
    discountScope: "order_total",
    /** order_total only: 'percent' | 'fixed' — tek genel iskonto türü */
    orderHeaderKind: "percent",
    orderDiscountPercent: 0,
    orderDiscountFixed: 0,
    exchangeRate: "",
  }));
  const [isOrderNameManual, setIsOrderNameManual] = useState(false);

  const fx = useExchangeRateOptional();

  const parseExchangeRate = (val) => {
    if (val === undefined || val === null || val === "") return undefined;
    const num = Number(String(val).trim().replace(",", "."));
    return !isNaN(num) && num > 0 ? num : undefined;
  };

  useEffect(() => {
    if (fx?.rate && !orderData.exchangeRate) {
      setOrderData((prev) => ({ ...prev, exchangeRate: String(fx.rate) }));
    }
  }, [fx?.rate]);

  const breadcrumbs = isEditMode
    ? [
        { label: "Rovion", url: "" },
        { label: "Teklifler", url: "/orders" },
        { label: "Teklif Düzenle" },
      ]
    : isRecreateMode
      ? [
          { label: "Rovion", url: "" },
          { label: "Teklifler", url: "/orders" },
          { label: "Teklifi Yeniden Oluştur" },
        ]
      : [
          { label: "Rovion", url: "" },
          { label: "Teklifler", url: "/orders" },
          { label: "Yeni Teklif" },
        ];

  const pageTitle = isEditMode
    ? "Teklif Düzenle"
    : isRecreateMode
      ? "Teklifi Yeniden Oluştur"
      : "Yeni Teklif Oluştur";

  useEffect(() => {
    fetchModels();
    fetchCompanies();
    fetchBankAccounts();
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, orderId]);

  useEffect(() => {
    if (!isRecreateMode || !recreateFromId) return;
    fetchOrderForRecreate(recreateFromId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecreateMode, recreateFromId]);

  const getMonthYearFromDate = (dateStr) => {
    const d = dateStr ? new Date(dateStr) : new Date();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear() % 100).padStart(2, "0");
    return `${month}${year}`; // MMYY
  };

  const buildOrderName = (companyCode, dateStr, sequence) => {
    if (!companyCode || !sequence) return "";
    const monthYear = getMonthYearFromDate(dateStr);
    return `${companyCode}/${monthYear}/${sequence}`;
  };

  const parseSequenceFromName = (name, monthYear) => {
    if (!name || typeof name !== "string") return 0;
    const parts = name.trim().split("/");
    if (parts.length !== 3 || parts[1] !== monthYear) return 0;
    const seq = parseInt(parts[2], 10);
    return Number.isFinite(seq) && seq > 0 ? seq : 0;
  };

  const fetchOrderSequenceForCompany = useCallback(
    async (companyId, dateStr) => {
      const headers = { Authorization: `Bearer ${getToken()}` };
      const params = { companyId };
      const monthYear = getMonthYearFromDate(dateStr);

      const [ordersRes, salesRes] = await Promise.all([
        axios.get(`${API_URL}/api/orders`, { params, headers }),
        axios.get(`${API_URL}/api/device-sales`, { params, headers }),
      ]);

      const ordersList = ordersRes.data?.orders;
      const salesList = salesRes.data?.sales;

      const blocked = new Set();

      if (Array.isArray(salesList)) {
        for (const s of salesList) {
          const seq = parseSequenceFromName(s?.saleName, monthYear);
          if (seq > 0) blocked.add(seq);
        }
      }

      // Keep rejected/cancelled order names reserved so the next offer gets a new sequence
      if (Array.isArray(ordersList)) {
        for (const o of ordersList) {
          const seq = parseSequenceFromName(o?.orderName, monthYear);
          if (seq > 0) blocked.add(seq);
        }
      }

      const blockedMax = blocked.size > 0 ? Math.max(...blocked) : 0;
      return blockedMax + 1;
    },
    [getToken],
  );

  // All existing order/sale names stay reserved (including rejected/cancelled)
  useEffect(() => {
    if (isEditMode) return;
    if (!orderData.companyId) {
      setOrderData((prev) => ({ ...prev, sequence: "", orderName: "" }));
      setIsOrderNameManual(false);
      return;
    }

    // Clear previous auto-generated values until we fetch the next sequence
    setOrderData((prev) => ({ ...prev, sequence: "", orderName: "" }));
    setIsOrderNameManual(false);

    let cancelled = false;
    (async () => {
      try {
        const seq = await fetchOrderSequenceForCompany(
          orderData.companyId,
          orderData.orderDate,
        );
        if (cancelled) return;
        setOrderData((prev) => ({
          ...prev,
          sequence: seq?.toString?.() || "",
        }));
      } catch (e) {
        console.error("Failed to generate order sequence:", e);
        if (cancelled) return;
        setOrderData((prev) => ({ ...prev, sequence: "", orderName: "" }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isEditMode,
    orderData.companyId,
    orderData.orderDate,
    fetchOrderSequenceForCompany,
  ]);

  // Auto-generate order name: "companyCode/MMYY/sequence"
  useEffect(() => {
    if (isEditMode) return;
    const company = companies.find((c) => c._id === orderData.companyId);
    if (!company?.code || !orderData.sequence) return;
    const nextOrderName = buildOrderName(
      company.code,
      orderData.orderDate,
      orderData.sequence,
    );
    if (!nextOrderName) return;
    setOrderData((prev) => ({
      ...prev,
      orderName: isOrderNameManual ? prev.orderName : nextOrderName,
    }));
  }, [
    orderData.companyId,
    orderData.orderDate,
    orderData.sequence,
    companies,
    isOrderNameManual,
  ]);

  const fetchModels = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/device-models`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setModels(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Failed to fetch models", e);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setCompanies(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Failed to fetch companies", e);
    }
  };

  const getDefaultBankAccountIds = useCallback((accounts) => {
    if (!Array.isArray(accounts) || accounts.length === 0) return [];
    const defaultIds = accounts
      .filter((account) => account.isDefault)
      .map((account) => String(account._id));
    if (defaultIds.length > 0) return defaultIds;
    return accounts[0]?._id ? [String(accounts[0]._id)] : [];
  }, []);

  const fetchBankAccounts = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/system-companies/me/bank-accounts`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const accounts = Array.isArray(res.data?.bankAccounts)
        ? res.data.bankAccounts
        : [];
      setBankAccounts(accounts);

      if (!isEditMode && !isRecreateMode && accounts.length > 0) {
        setOrderData((prev) => ({
          ...prev,
          bankAccountIds:
            prev.bankAccountIds?.length > 0
              ? prev.bankAccountIds
              : getDefaultBankAccountIds(accounts),
        }));
      }
    } catch (e) {
      console.error("Failed to fetch bank accounts", e);
      setBankAccounts([]);
    }
  };

  const fetchOrder = async () => {
    try {
      setFetchingOrder(true);
      const res = await axios.get(`${API_URL}/api/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const order = res.data?.order || res.data;
      if (!order) throw new Error("Teklif bulunamadı");

      if (order.status !== "pending") {
        addToast("Sadece bekleyen Teklifler düzenlenebilir.", "danger");
        setTimeout(() => navigate("/orders"), 1500);
        return;
      }

      setOrderData(buildOrderFormStateFromOrder(order));
      setIsOrderNameManual(true);
      setSelectedModels(mapOrderModelsToSelected(order));
    } catch (e) {
      addToast(e.response?.data?.message || "Teklif yüklenemedi.", "danger");
    } finally {
      setFetchingOrder(false);
    }
  };

  const fetchOrderForRecreate = async (sourceOrderId) => {
    try {
      setFetchingOrder(true);
      const res = await axios.get(`${API_URL}/api/orders/${sourceOrderId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const order = res.data?.order || res.data;
      if (!order) throw new Error("Teklif bulunamadı");

      if (order.status !== "rejected") {
        addToast("Yalnızca reddedilen teklifler yeniden oluşturulabilir.", "danger");
        setTimeout(() => navigate("/orders"), 1500);
        return;
      }

      setOrderData(buildOrderFormStateFromOrder(order, { useToday: true }));
      setIsOrderNameManual(false);
      setSelectedModels(mapOrderModelsToSelected(order));
    } catch (e) {
      addToast(
        e.response?.data?.message || "Teklif bilgileri yüklenemedi.",
        "danger",
      );
      setTimeout(() => navigate("/orders"), 1500);
    } finally {
      setFetchingOrder(false);
    }
  };

  const handleAddModel = (model) => {
    const idx = selectedModels.findIndex((m) => m.modelId === model._id);
    if (idx >= 0) {
      const updated = [...selectedModels];
      updated[idx].count += 1;
      setSelectedModels(updated);
    } else {
      setSelectedModels([
        ...selectedModels,
        {
          modelId: model._id,
          code: model.code,
          name: model.name,
          count: 1,
          price: model.price || 0,
          discountPercent: 0,
          terminType: "stock",
          terminDate: "",
        },
      ]);
    }
  };

  const handleRemoveModel = (modelId) => {
    setSelectedModels(selectedModels.filter((m) => m.modelId !== modelId));
  };

  const handleUpdateCount = (modelId, count) => {
    if (count <= 0) {
      handleRemoveModel(modelId);
      return;
    }
    setSelectedModels(
      selectedModels.map((m) => (m.modelId === modelId ? { ...m, count } : m)),
    );
  };

  const handleUpdateModelTermin = (modelId, patch) => {
    setSelectedModels((prev) =>
      prev.map((m) => {
        if (m.modelId !== modelId) return m;
        const next = { ...m, ...patch };
        if (next.terminType === "date" && !next.terminDate) {
          next.terminDate =
            orderData.terminTarihi || defaultTerminTarihi(orderData.orderDate);
        }
        if (next.terminType === "stock") {
          next.terminDate = "";
        }
        return next;
      }),
    );
  };

  const handleOrderDateChange = async (v) => {
    setOrderData((p) => ({
      ...p,
      orderDate: v,
      terminTarihi: defaultTerminTarihi(v),
    }));
    if (v) {
      try {
        const rateRes = await axios.get(
          `${API_URL}/api/exchange-rate/by-date?date=${v}`,
          { headers: { Authorization: `Bearer ${getToken()}` } },
        );
        if (rateRes.data?.rate) {
          setOrderData((prev) => ({
            ...prev,
            exchangeRate: String(rateRes.data.rate),
          }));
        }
      } catch (err) {
        console.warn("Could not fetch rate for date:", err);
      }
    }
  };

  const currentExchangeRate =
    parseExchangeRate(orderData.exchangeRate) ?? fx?.rate;

  const discountOpts = useMemo(() => {
    const list = calculateListSubtotal(selectedModels);
    return {
      discountScope: orderData.discountScope,
      orderHeaderKind: orderData.orderHeaderKind,
      orderDiscountPercent: orderData.orderDiscountPercent,
      orderDiscountFixed: orderData.orderDiscountFixed,
      listSubtotal: list,
    };
  }, [
    selectedModels,
    orderData.discountScope,
    orderData.orderHeaderKind,
    orderData.orderDiscountPercent,
    orderData.orderDiscountFixed,
  ]);

  const companySelectOptions = useMemo(
    () =>
      companies.map((c) => ({
        value: c._id,
        label: `${c.name} (${c.code})`,
      })),
    [companies],
  );

  const lineNetTotal = (m) => getLineDiscountInfo(m, discountOpts).net;

  const calcFinalTotal = () =>
    calculateFinalTotal(selectedModels, discountOpts);

  const calcListSubtotal = () => calculateListSubtotal(selectedModels);

  const calcDiscountAmount = () =>
    roundMoney(Math.max(0, calcListSubtotal() - calcFinalTotal()));

  const calcKdvAmount = () => roundMoney(calcFinalTotal() * KDV_RATE);

  const calcGrandTotalWithKdv = () =>
    roundMoney(calcFinalTotal() + calcKdvAmount());

  const hasActiveDiscount = useMemo(() => {
    if (orderData.discountScope === "order_total") {
      if (orderData.orderHeaderKind === "percent") {
        return clampPct(orderData.orderDiscountPercent) > 0;
      }
      return (Number(orderData.orderDiscountFixed) || 0) > 0;
    }
    return selectedModels.some((m) => clampPct(m.discountPercent) > 0);
  }, [
    orderData.discountScope,
    orderData.orderHeaderKind,
    orderData.orderDiscountPercent,
    orderData.orderDiscountFixed,
    selectedModels,
  ]);

  const clearAllDiscounts = () => {
    setOrderData((p) => ({
      ...p,
      discountScope: "per_line",
      orderDiscountPercent: 0,
      orderDiscountFixed: 0,
      orderHeaderKind: "percent",
    }));
    setSelectedModels((prev) =>
      prev.map((m) => ({ ...m, discountPercent: 0 })),
    );
    setBulkDiscountModelIds([]);
    setBulkDiscountPercent("");
  };

  const setDiscountScope = (scope) => {
    if (scope === "order_total") {
      setOrderData((p) => ({ ...p, discountScope: "order_total" }));
      setSelectedModels((prev) =>
        prev.map((m) => ({ ...m, discountPercent: 0 })),
      );
      setBulkDiscountModelIds([]);
      setBulkDiscountPercent("");
    } else {
      setOrderData((p) => ({
        ...p,
        discountScope: "per_line",
        orderDiscountPercent: 0,
        orderDiscountFixed: 0,
        orderHeaderKind: "percent",
      }));
    }
  };

  const handleLineDiscountChange = (modelId, raw) => {
    const n = Math.min(
      100,
      Math.max(0, parseFloat(String(raw).replace(",", ".")) || 0),
    );
    setSelectedModels((prev) =>
      prev.map((m) =>
        m.modelId === modelId ? { ...m, discountPercent: n } : m,
      ),
    );
  };

  const toggleBulkDiscountModel = (modelId, checked) => {
    setBulkDiscountModelIds((prev) => {
      if (checked) {
        return prev.includes(modelId) ? prev : [...prev, modelId];
      }
      return prev.filter((id) => id !== modelId);
    });
  };

  const toggleSelectAllBulkDiscountModels = (checked) => {
    if (checked) {
      setBulkDiscountModelIds(selectedModels.map((m) => m.modelId));
    } else {
      setBulkDiscountModelIds([]);
    }
  };

  const applyBulkLineDiscount = () => {
    if (bulkDiscountModelIds.length === 0) {
      addToast("Önce en az bir model seçin.", "danger");
      return;
    }
    const n = Math.min(
      100,
      Math.max(
        0,
        parseFloat(String(bulkDiscountPercent).replace(",", ".")) || 0,
      ),
    );
    const idSet = new Set(bulkDiscountModelIds);
    setSelectedModels((prev) =>
      prev.map((m) =>
        idSet.has(m.modelId) ? { ...m, discountPercent: n } : m,
      ),
    );
    setBulkDiscountPercent("");
  };

  const openDiscountModal = () => {
    setBulkDiscountModelIds([]);
    setBulkDiscountPercent("");
    setShowDiscountModal(true);
  };

  const addToast = (description, appearance = "success") => {
    setToasts([
      {
        id: Date.now(),
        description,
        appearance,
        title: appearance === "success" ? "Başarılı!" : "Hata!",
      },
    ]);
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  const buildDiscountPayload = () => {
    const scope = orderData.discountScope || "per_line";
    if (scope === "order_total") {
      if (orderData.orderHeaderKind === "percent") {
        return {
          discountScope: "order_total",
          orderDiscountPercent: clampPct(orderData.orderDiscountPercent),
          orderDiscountFixed: 0,
        };
      }
      return {
        discountScope: "order_total",
        orderDiscountPercent: 0,
        orderDiscountFixed: Math.max(
          0,
          Number(orderData.orderDiscountFixed) || 0,
        ),
      };
    }
    return {
      discountScope: "per_line",
      orderDiscountPercent: 0,
      orderDiscountFixed: 0,
    };
  };

  const handleSubmit = async () => {
    if (!orderData.companyId) {
      addToast("Firma seçimi zorunludur.", "danger");
      return;
    }
    if (!orderData.orderName.trim()) {
      addToast("Teklif adı gereklidir.", "danger");
      return;
    }
    if (selectedModels.length === 0) {
      addToast("En az bir model seçmelisiniz.", "danger");
      return;
    }
    if (
      selectedModels.some(
        (m) => m.terminType === "date" && !String(m.terminDate || "").trim(),
      )
    ) {
      addToast("Tarih terminli modeller için termin tarihi seçin.", "danger");
      return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        const disc = buildDiscountPayload();
        const payload = {
          orderName: orderData.orderName.trim(),
          companyId: orderData.companyId || undefined,
          orderDate: orderData.orderDate,
          terminTarihi: orderData.terminTarihi || undefined,
          paymentMethod: orderData.paymentMethod,
          pdfDescription: orderData.pdfDescription?.trim() || "",
          yetkiliKisi: orderData.yetkiliKisi?.trim() || "",
          bankAccountIds: orderData.bankAccountIds || [],
          teslimatYontemi: orderData.teslimatYontemi?.trim() || "",
          opsiyonSuresi: orderData.opsiyonSuresi?.trim() || "",
          ekBilgiNotu: orderData.ekBilgiNotu?.trim() || "",
          exchangeRate:
            parseExchangeRate(orderData.exchangeRate) ??
            parseExchangeRate(fx?.rate) ??
            undefined,
          totalAmount: calcFinalTotal(),
          ...disc,
          models: selectedModels.map((m) => ({
            modelId: m.modelId,
            count: m.count,
            discountPercent: clampPct(m.discountPercent),
            ...mapModelTerminForSave(m, orderData.terminTarihi),
          })),
        };

        const res = await axios.put(
          `${API_URL}/api/orders/${orderId}`,
          payload,
          {
            headers: { Authorization: `Bearer ${getToken()}` },
          },
        );

        if (res.data.success) {
          addToast("Teklif başarıyla güncellendi!", "success");
          setTimeout(() => navigate("/orders"), 1500);
        }
        return;
      }

      let sequenceToUse = orderData.sequence;
      if (!sequenceToUse && orderData.companyId) {
        try {
          sequenceToUse = (
            await fetchOrderSequenceForCompany(
              orderData.companyId,
              orderData.orderDate,
            )
          )?.toString?.();
          setOrderData((prev) => ({ ...prev, sequence: sequenceToUse }));
        } catch (e) {
          addToast("Sıra numarası üretilemedi.", "danger");
          return;
        }
      }

      const company = companies.find((c) => c._id === orderData.companyId);
      const expectedOrderName =
        company?.code && sequenceToUse
          ? buildOrderName(company.code, orderData.orderDate, sequenceToUse)
          : "";
      const orderNameToUse = orderData.orderName?.trim()
        ? orderData.orderName
        : expectedOrderName;
      if (!orderNameToUse || orderNameToUse.trim() === "") {
        addToast("Teklif adı gereklidir.", "danger");
        return;
      }

      const disc = buildDiscountPayload();
      const payload = {
        orderName: orderNameToUse.trim(),
        companyId: orderData.companyId || undefined,
        orderDate: orderData.orderDate,
        exchangeRate:
          parseExchangeRate(orderData.exchangeRate) ??
          parseExchangeRate(fx?.rate) ??
          undefined,
        terminTarihi: orderData.terminTarihi || undefined,
        paymentMethod: orderData.paymentMethod,
        pdfDescription: orderData.pdfDescription?.trim() || "",
        yetkiliKisi: orderData.yetkiliKisi?.trim() || "",
        bankAccountIds: orderData.bankAccountIds || [],
        teslimatYontemi: orderData.teslimatYontemi?.trim() || "",
        opsiyonSuresi: orderData.opsiyonSuresi?.trim() || "",
        ekBilgiNotu: orderData.ekBilgiNotu?.trim() || "",
        totalAmount: calcFinalTotal(),
        ...disc,
        models: selectedModels.map((m) => ({
          modelId: m.modelId,
          count: m.count,
          discountPercent: clampPct(m.discountPercent),
          ...mapModelTerminForSave(m, orderData.terminTarihi),
        })),
      };

      const res = await axios.post(`${API_URL}/api/orders`, payload, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      if (res.data.success) {
        addToast("Teklif başarıyla oluşturuldu!", "success");
        setTimeout(() => navigate("/orders"), 1500);
      }
    } catch (err) {
      addToast(
        err.response?.data?.message || "Teklif oluşturulamadı.",
        "danger",
      );
    } finally {
      setLoading(false);
    }
  };

  if (!canWrite) {
    return (
      <AppPage title={pageTitle} breadcrumbs={breadcrumbs}>
        <Container>
          <ContainerItem>
            <div className="alert alert-warning d-flex justify-content-between align-items-center">
              <span>Bu sayfada değişiklik yapma yetkiniz yok.</span>
              <Button
                appearance="primary-animated"
                onClick={() => navigate("/orders")}
              >
                Geri Dön
              </Button>
            </div>
          </ContainerItem>
        </Container>
      </AppPage>
    );
  }

  if ((isEditMode || isRecreateMode) && fetchingOrder) {
    return (
      <AppPage title={pageTitle} breadcrumbs={breadcrumbs}>
        <Container>
          <ContainerItem>
            <div className="d-flex justify-content-center align-items-center p-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          </ContainerItem>
        </Container>
      </AppPage>
    );
  }

  return (
    <AppPage title={pageTitle} breadcrumbs={breadcrumbs}>
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="card shadow-sm modern-card">
            <div className="card-body p-4">
              <>
                {/* Teklif Bilgileri */}
                <div className="section-container">
                  <div className="section-header">
                    <div className="section-icon">
                      <FiShoppingBag className="text-primary" />
                    </div>
                    <div className="section-title flex-grow-1">
                      <h5 className="mb-1">Teklif Bilgileri</h5>
                      <p className="text-muted small mb-0">
                        Teklif adı, cari ve tarih bilgilerini girin
                      </p>
                    </div>
                  </div>
                  <div className="section-content">
                    <div className="d-flex justify-content-start flex-wrap gap-3">
                      <div className="col-md-4 w-45">
                        <Label>
                          Firma <span className="text-danger">*</span>
                        </Label>
                        <div className="form-control-container">
                          <Select
                            unique
                            placeholder="Firma seçin..."
                            options={companySelectOptions}
                            selected={
                              orderData.companyId ? [orderData.companyId] : []
                            }
                            searchInDropdown
                            searchPlaceholder="Firma ara..."
                            onSelected={(values) => {
                              const selectedId = values?.[0]?.value ?? null;
                              const company = companies.find(
                                (c) => c._id === selectedId,
                              );
                              setOrderData((p) => ({
                                ...p,
                                companyId: selectedId,
                                yetkiliKisi: company?.yetkiliKisi || "",
                              }));
                            }}
                          />
                        </div>
                      </div>
                      <div className="col-md-4 w-45">
                        <Label>
                          Teklif Adı <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          value={orderData.orderName}
                          onChange={(v) => {
                            setIsOrderNameManual(true);
                            setOrderData((p) => ({ ...p, orderName: v }));
                          }}
                          placeholder="Teklif adını girin"
                        />
                      </div>
                      <div className="col-md-4 w-45">
                        <Input
                          type="date"
                          label="Teklif Tarihi"
                          value={orderData.orderDate}
                          onChange={handleOrderDateChange}
                          prefix={<FiCalendar className="text-muted" />}
                        />
                      </div>
                      <div className="col-md-4 w-45">
                        <Input
                          type="text"
                          inputMode="decimal"
                          label="Döviz Kuru (USD/TRY)"
                          value={orderData.exchangeRate}
                          onChange={(value) =>
                            setOrderData((prev) => ({
                              ...prev,
                              exchangeRate: value,
                            }))
                          }
                          prefix={<FiDollarSign className="text-muted" />}
                          placeholder={fx?.rate ? String(fx.rate) : "42.156"}
                          hint="TCMB USD/TRY döviz kuru (Örn: 42.156 veya 42,156)"
                        />
                      </div>
                      <div className="col-md-4 w-45">
                        <Input
                          type="date"
                          label="Termin Tarihi"
                          value={orderData.terminTarihi}
                          onChange={(v) =>
                            setOrderData((p) => ({ ...p, terminTarihi: v }))
                          }
                          prefix={<FiCalendar className="text-muted" />}
                        />
                      </div>
                      <div className="col-md-4 w-45">
                        <Input
                          type="text"
                          label="Yetkili Kişi"
                          value={orderData.yetkiliKisi}
                          onChange={(v) =>
                            setOrderData((p) => ({ ...p, yetkiliKisi: v }))
                          }
                          placeholder="Firma yetkilisini girin"
                          prefix={<FiUser className="text-muted" />}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Model Seçimi */}
                <div className="section-container">
                  <div className="section-header d-flex justify-content-between w-100">
                    <div className="d-flex align-items-center gap-2">
                      <div className="section-icon">
                        <FiServer />
                      </div>
                      <div className="section-title flex-grow-1">
                        <h5 className="mb-1">Model Seçimi</h5>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Button
                        type="button"
                        appearance={
                          hasActiveDiscount ? "primary" : "primary-subtle"
                        }
                        icon={<FiPercent />}
                        onClick={openDiscountModal}
                        disabled={selectedModels.length === 0}
                      >
                        İskonto Ayarları
                      </Button>
                      <Button
                        appearance="primary"
                        icon={<FiPackage />}
                        onClick={() => setShowModelPicker(true)}
                      >
                        Model Ekle
                      </Button>
                    </div>
                  </div>
                  <div className="section-content">
                    {selectedModels.length === 0 ? (
                      <div className="empty-models-container">
                        <FiServer size={48} className="text-muted opacity-50" />
                        <p className="text-muted mt-3 mb-0">
                          Model seçilmedi. "Model Ekle" butonuna tıklayın.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2"></div>
                        <div className="models-grid">
                          {selectedModels.map((item) => (
                            <div key={item.modelId} className="model-card">
                              <div className="model-card-header d-flex justify-content-between w-100">
                                <div className="d-flex align-items-center gap-2">
                                  <div className="model-icon-wrapper">
                                    <FiPackage className="model-icon" />
                                  </div>
                                  <div className="model-info flex-grow-1">
                                    <div className="model-code">
                                      {item.code}
                                    </div>
                                    <div
                                      className="model-name-text"
                                      title={item.name || undefined}
                                    >
                                      {limitWords(item.name)}
                                    </div>
                                  </div>
                                </div>
                                <IconButton
                                  icon={<FiTrash2 />}
                                  appearance="danger-subtle"
                                  onClick={() =>
                                    handleRemoveModel(item.modelId)
                                  }
                                />
                              </div>
                              <div className="model-card-divider" />
                              <div className="model-card-body">
                                <div className="model-detail-row">
                                  <span className="detail-label">Adet:</span>
                                  <div className="count-container">
                                    <Button
                                      appearance="subtle"
                                      size="sm"
                                      onClick={() =>
                                        handleUpdateCount(
                                          item.modelId,
                                          Math.max(1, item.count - 1),
                                        )
                                      }
                                    >
                                      -
                                    </Button>
                                    <Input
                                      type="number"
                                      value={item.count.toString()}
                                      onChange={(v) => {
                                        const n =
                                          parseInt(v.replace(/[^0-9]/g, "")) ||
                                          1;
                                        handleUpdateCount(item.modelId, n);
                                      }}
                                      className="count-input"
                                      min={1}
                                    />
                                    <Button
                                      appearance="subtle"
                                      size="sm"
                                      onClick={() =>
                                        handleUpdateCount(
                                          item.modelId,
                                          item.count + 1,
                                        )
                                      }
                                    >
                                      +
                                    </Button>
                                  </div>
                                </div>
                                <div className="model-detail-row">
                                  <span className="detail-label">
                                    Birim Fiyat:
                                  </span>
                                  <span className="model-price">
                                    <ProductPrice
                                      value={item.price || 0}
                                      rate={currentExchangeRate}
                                    />
                                  </span>
                                </div>
                                <div className="model-detail-row">
                                  <span className="detail-label">Termin:</span>
                                  <div className="model-termin-inline">
                                    <div className="model-termin-segment">
                                      <button
                                        type="button"
                                        className={
                                          item.terminType !== "date"
                                            ? "active"
                                            : ""
                                        }
                                        onClick={() =>
                                          handleUpdateModelTermin(item.modelId, {
                                            terminType: "stock",
                                          })
                                        }
                                      >
                                        Stokta
                                      </button>
                                      <button
                                        type="button"
                                        className={
                                          item.terminType === "date"
                                            ? "active"
                                            : ""
                                        }
                                        onClick={() =>
                                          handleUpdateModelTermin(item.modelId, {
                                            terminType: "date",
                                          })
                                        }
                                      >
                                        Tarih
                                      </button>
                                    </div>
                                    <div className="model-termin-value">
                                      <span
                                        className={`model-termin-stock-label${
                                          item.terminType === "date"
                                            ? " is-hidden"
                                            : ""
                                        }`}
                                      >
                                        STOKTA
                                      </span>
                                      <input
                                        type="date"
                                        className={`model-termin-date-native${
                                          item.terminType !== "date"
                                            ? " is-hidden"
                                            : ""
                                        }`}
                                        value={item.terminDate || ""}
                                        tabIndex={
                                          item.terminType === "date" ? 0 : -1
                                        }
                                        onChange={(e) =>
                                          handleUpdateModelTermin(
                                            item.modelId,
                                            { terminDate: e.target.value },
                                          )
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                                {hasActiveDiscount ? (
                                  (() => {
                                    const lineDisc = getLineDiscountInfo(
                                      item,
                                      discountOpts,
                                    );
                                    return (
                                      <>
                                        <div className="model-detail-row">
                                          <span className="detail-label">
                                            Liste tutarı:
                                          </span>
                                          <span className="text-muted">
                                            <ProductPrice
                                              value={lineDisc.gross}
                                              rate={currentExchangeRate}
                                            />
                                          </span>
                                        </div>
                                        <div className="model-detail-row">
                                          <span className="detail-label">
                                            İskonto:
                                          </span>
                                          <span className="text-end">
                                            {orderData.discountScope ===
                                            "per_line" ? (
                                              lineDisc.percent > 0 ? (
                                                <span className="text-success fw-semibold">
                                                  %
                                                  {format(
                                                    lineDisc.percent,
                                                    "number",
                                                  )}
                                                </span>
                                              ) : (
                                                <span className="text-muted">
                                                  —
                                                </span>
                                              )
                                            ) : orderData.orderHeaderKind ===
                                              "percent" ? (
                                              <span className="text-success fw-semibold">
                                                %
                                                {format(
                                                  lineDisc.percent,
                                                  "number",
                                                )}
                                              </span>
                                            ) : lineDisc.amount > 0 ? (
                                              <span className="text-success fw-semibold">
                                                −
                                                <ProductPrice
                                                  value={lineDisc.amount}
                                                  rate={currentExchangeRate}
                                                />
                                              </span>
                                            ) : (
                                              <span className="text-muted">
                                                —
                                              </span>
                                            )}
                                          </span>
                                        </div>
                                        <div className="model-total-row">
                                          <span className="detail-label fw-bold">
                                            Model net:
                                          </span>
                                          <span className="model-total">
                                            <ProductPrice
                                              value={lineNetTotal(item)}
                                              rate={currentExchangeRate}
                                            />
                                          </span>
                                        </div>
                                      </>
                                    );
                                  })()
                                ) : (
                                  <div className="model-total-row">
                                    <span className="detail-label fw-bold">
                                      Tutar:
                                    </span>
                                    <span className="model-total">
                                      <ProductPrice
                                        value={lineNetTotal(item)}
                                        rate={currentExchangeRate}
                                      />
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="total-amount-container mt-3">
                          <div className="d-flex flex-column gap-2 mt-3">
                            {hasActiveDiscount && (
                              <>
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-muted small">
                                    Liste ara toplamı:
                                  </span>
                                  <span className="fw-semibold">
                                    <ProductPrice
                                      value={calcListSubtotal()}
                                      rate={currentExchangeRate}
                                    />
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-muted small">
                                    İskonto
                                    {orderData.discountScope === "order_total" &&
                                    orderData.orderHeaderKind === "percent" &&
                                    clampPct(orderData.orderDiscountPercent) > 0
                                      ? ` (%${format(
                                          clampPct(
                                            orderData.orderDiscountPercent,
                                          ),
                                          "number",
                                        )})`
                                      : ""}
                                    :
                                  </span>
                                  <span className="fw-semibold text-danger">
                                    −
                                    <ProductPrice
                                      value={calcDiscountAmount()}
                                      rate={currentExchangeRate}
                                    />
                                  </span>
                                </div>
                              </>
                            )}
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="text-muted small">
                                Ara toplam:
                              </span>
                              <span className="fw-semibold">
                                <ProductPrice
                                  value={calcFinalTotal()}
                                  rate={currentExchangeRate}
                                />
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="text-muted small">
                                KDV (%20):
                              </span>
                              <span className="fw-semibold">
                                <ProductPrice
                                  value={calcKdvAmount()}
                                  rate={currentExchangeRate}
                                />
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center pt-3 mt-1 border-top">
                              <span className="total-amount-label">
                                Genel Toplam:
                              </span>
                              <span className="total-amount-value">
                                <ProductPrice
                                  value={calcGrandTotalWithKdv()}
                                  rate={currentExchangeRate}
                                />
                              </span>
                            </div>
                            {currentExchangeRate && (
                              <div className="exchange-rate-info text-muted small mt-2">
                                <span>
                                  Döviz Kuru: 1 USD ={" "}
                                  {Number(currentExchangeRate).toLocaleString(
                                    "tr-TR",
                                    {
                                      minimumFractionDigits: 4,
                                      maximumFractionDigits: 4,
                                    },
                                  )}{" "}
                                  TL
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Ödeme Bilgileri */}
                <div className="section-container">
                  <div className="section-header">
                    <div className="section-icon">
                      <FiCreditCard />
                    </div>
                    <div className="section-title flex-grow-1">
                      <h5 className="mb-1">Ödeme Bilgileri</h5>
                    </div>
                  </div>
                  <div className="section-content">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <Label>Ödeme Yöntemi</Label>
                        <div className="form-control-container">
                          <Dropdown
                            trigger={
                              <div className="d-flex align-items-center justify-content-between p-2">
                                <div className="d-flex align-items-center gap-2">
                                  <FiDollarSign className="text-muted" />
                                  <span>
                                    {
                                      paymentMethodLabels[
                                        orderData.paymentMethod
                                      ]
                                    }
                                  </span>
                                </div>
                              </div>
                            }
                          >
                            <DropdownContent>
                              {Object.entries(paymentMethodLabels).map(
                                ([key, label]) => (
                                  <DropdownItem
                                    key={key}
                                    onClick={() =>
                                      setOrderData((p) => ({
                                        ...p,
                                        paymentMethod: key,
                                      }))
                                    }
                                    active={orderData.paymentMethod === key}
                                  >
                                    {label}
                                  </DropdownItem>
                                ),
                              )}
                            </DropdownContent>
                          </Dropdown>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <Input
                          type="text"
                          label="Ek Bilgi Notu"
                          value={orderData.ekBilgiNotu}
                          onChange={(v) =>
                            setOrderData((p) => ({ ...p, ekBilgiNotu: v }))
                          }
                          placeholder="FİYATLAR, 24 SAAT İÇİN GEÇERLİDİR."
                        />
                      </div>
                      <div className="col-md-6">
                        <Label>Teslimat Yöntemi</Label>
                        <div className="form-control-container">
                          <Dropdown
                            trigger={
                              <div className="d-flex align-items-center justify-content-between p-2">
                                <span>
                                  {TESLIMAT_YONTEMI_OPTIONS.find(
                                    (o) =>
                                      o.value === orderData.teslimatYontemi,
                                  )?.label ||
                                    orderData.teslimatYontemi ||
                                    "Seçin..."}
                                </span>
                              </div>
                            }
                          >
                            <DropdownContent>
                              {TESLIMAT_YONTEMI_OPTIONS.map((opt) => (
                                <DropdownItem
                                  key={opt.value}
                                  onClick={() =>
                                    setOrderData((p) => ({
                                      ...p,
                                      teslimatYontemi: opt.value,
                                    }))
                                  }
                                  active={
                                    orderData.teslimatYontemi === opt.value
                                  }
                                >
                                  {opt.label}
                                </DropdownItem>
                              ))}
                            </DropdownContent>
                          </Dropdown>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <Input
                          type="text"
                          label="Opsiyon Süresi"
                          value={orderData.opsiyonSuresi}
                          onChange={(v) =>
                            setOrderData((p) => ({ ...p, opsiyonSuresi: v }))
                          }
                          placeholder="3 İŞ GÜNÜ"
                        />
                      </div>
                      <div className="col-12 mt-3">
                        <Input
                          type="textarea"
                          label="Açıklama"
                          value={orderData.pdfDescription}
                          onChange={(v) =>
                            setOrderData((p) => ({ ...p, pdfDescription: v }))
                          }
                          rows={16}
                          placeholder="Sipariş koşulları ve ödeme şartları..."
                          style={{
                            fontFamily:
                              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                            fontSize: "12px",
                          }}
                        />
                        <p className="text-muted small mt-2 mb-0">
                          PDF&apos;deki sipariş koşulları bölümü bu alanda
                          düzenlenir.
                        </p>
                      </div>
                      <div className="col-12 mt-3">
                        <Label>Banka Hesap Bilgileri</Label>
                        {bankAccounts.length > 0 ? (
                          <div className="mt-2" style={{ maxWidth: 520 }}>
                            <Select
                              label="Hesap Seç"
                              placeholder="PDF için banka hesapları seçin..."
                              options={bankAccounts.map((a) => ({
                                value: String(a._id),
                                label: `${a.label || "Hesap"} — ${a.bankIban || ""}`,
                              }))}
                              selected={orderData.bankAccountIds || []}
                              searchInDropdown
                              searchPlaceholder="Hesap ara..."
                              onSelected={(values) => {
                                setOrderData((p) => ({
                                  ...p,
                                  bankAccountIds: (values || []).map((v) =>
                                    String(v.value),
                                  ),
                                }));
                              }}
                            />
                            <p className="text-muted small mt-2 mb-0">
                              PDF&apos;de gösterilecek hesapları seçin. Birden
                              fazla hesap seçebilirsiniz.
                            </p>
                          </div>
                        ) : (
                          <p className="text-muted small mt-2 mb-0">
                            Tanımlı banka hesabı yok. Banka Hesapları
                            sayfasından hesap ekleyebilirsiniz.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Butonlar */}
                <div className="form-actions">
                  <Button
                    appearance="danger-subtle"
                    onClick={() => navigate("/orders")}
                  >
                    İptal
                  </Button>
                  <Button
                    appearance="primary-animated"
                    icon={<FiShoppingBag />}
                    onClick={handleSubmit}
                    loading={loading}
                  >
                    {isEditMode ? "Teklifi Güncelle" : "Teklifi Oluştur"}
                  </Button>
                </div>
              </>
            </div>
          </div>
        </ContainerItem>
      </Container>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
          />
        ))}
      </ToastContainer>

      {/* Model Picker Modal */}
      <Modal
        isOpen={showModelPicker}
        onClose={() => setShowModelPicker(false)}
        scrollBehavior="inside"
        width="medium"
      >
        <ModalHeader>
          <div className="d-flex align-items-center gap-2">
            <div className="modal-header-icon">
              <FiPackage />
            </div>
            <div>
              <h5 className="mb-0">Model Seç</h5>
              <small className="text-muted">
                Teklife eklemek istediğiniz modeli seçin
              </small>
            </div>
          </div>
        </ModalHeader>
        <ModalBody>
          {models.length === 0 ? (
            <div className="empty-models-container">
              <FiServer size={48} className="text-muted opacity-50" />
              <p className="text-muted mt-3 mb-0">Model bulunamadı</p>
            </div>
          ) : (
            models.map((model) => (
              <div
                key={model._id}
                className="model-picker-item"
                onClick={() => handleAddModel(model)}
              >
                <div className="d-flex align-items-center gap-3 flex-grow-1">
                  <div className="model-picker-icon">
                    <FiPackage />
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-semibold">
                      {model.code} — {model.name}
                    </div>
                    <div className="text-muted small">
                      <ProductPrice value={model.price || 0} />
                    </div>
                  </div>
                </div>
                <div className="model-picker-action">
                  <FiCheckCircle className="text-primary" />
                </div>
              </div>
            ))
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            appearance="danger-subtle"
            onClick={() => setShowModelPicker(false)}
          >
            Kapat
          </Button>
        </ModalFooter>
      </Modal>

      {/* Discount Settings Modal */}
      <Modal
        isOpen={showDiscountModal}
        onClose={() => setShowDiscountModal(false)}
        scrollBehavior="inside"
        width="medium"
      >
        <ModalHeader>
          <div className="d-flex align-items-center gap-2">
            <div className="modal-header-icon">
              <FiPercent />
            </div>
            <div>
              <h5 className="mb-0">İskonto Ayarları</h5>
              <small className="text-muted">
                Teklif için iskonto türünü ve oranlarını belirleyin
              </small>
            </div>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="mb-3 p-3 rounded border bg-white">
            <Label className="mb-2 d-block">İskonto Türü</Label>
            <div className="d-flex flex-wrap gap-2">
              <Button
                type="button"
                appearance={
                  orderData.discountScope === "order_total"
                    ? "primary"
                    : "primary-subtle"
                }
                onClick={() => setDiscountScope("order_total")}
              >
                Genel toplam (liste fiyatı üzerinden)
              </Button>
              <Button
                type="button"
                appearance={
                  orderData.discountScope === "per_line"
                    ? "primary"
                    : "primary-subtle"
                }
                onClick={() => setDiscountScope("per_line")}
              >
                Model bazlı (%)
              </Button>
            </div>
          </div>

          {orderData.discountScope === "order_total" ? (
            <div className="d-flex flex-wrap gap-2 align-items-end mb-2">
              <div className="flex-grow-1" style={{ minWidth: 180 }}>
                <Select
                  className="mb-0"
                  label="Genel iskonto türü"
                  unique
                  options={orderHeaderKindSelectOptions}
                  selected={[orderData.orderHeaderKind]}
                  onSelected={(values) => {
                    const v = values?.[0]?.value;
                    if (v === "percent") {
                      setOrderData((p) => ({
                        ...p,
                        orderHeaderKind: "percent",
                        orderDiscountFixed: 0,
                      }));
                    } else if (v === "fixed") {
                      setOrderData((p) => ({
                        ...p,
                        orderHeaderKind: "fixed",
                        orderDiscountPercent: 0,
                      }));
                    }
                  }}
                  placeholder="Tür seçin"
                />
              </div>
              <div className="flex-grow-1" style={{ minWidth: 160 }}>
                {orderData.orderHeaderKind === "percent" ? (
                  <Input
                    type="number"
                    label="İskonto (%)"
                    value={String(orderData.orderDiscountPercent ?? 0)}
                    onChange={(v) =>
                      setOrderData((p) => ({
                        ...p,
                        orderDiscountPercent: Math.min(
                          100,
                          Math.max(
                            0,
                            parseFloat(
                              v.replace(/[^0-9.,]/g, "").replace(",", "."),
                            ) || 0,
                          ),
                        ),
                      }))
                    }
                    min={0}
                    max={100}
                    prefix={<FiPercent className="text-muted" />}
                  />
                ) : (
                  <Input
                    type="number"
                    label="İskonto (USD)"
                    value={String(orderData.orderDiscountFixed ?? 0)}
                    onChange={(v) =>
                      setOrderData((p) => ({
                        ...p,
                        orderDiscountFixed: Math.max(
                          0,
                          parseFloat(
                            v.replace(/[^0-9.,]/g, "").replace(",", "."),
                          ) || 0,
                        ),
                      }))
                    }
                    min={0}
                    prefix={<FiDollarSign className="text-muted" />}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {selectedModels.length === 0 ? (
                <p className="text-muted mb-0">Önce model ekleyin.</p>
              ) : (
                <>
                  <div className="p-3 rounded border bg-light">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <span className="small fw-semibold text-nowrap">
                        Toplu iskonto (%)
                      </span>
                      <div style={{ width: 100 }}>
                        <Input
                          type="number"
                          placeholder="0"
                          value={bulkDiscountPercent}
                          onChange={(v) =>
                            setBulkDiscountPercent(v.replace(/[^0-9.,]/g, ""))
                          }
                          className="discount-input mb-0"
                          min={0}
                          max={100}
                          prefix={<FiPercent className="text-muted" />}
                        />
                      </div>
                      <Button
                        type="button"
                        appearance="primary"
                        onClick={applyBulkLineDiscount}
                        disabled={bulkDiscountModelIds.length === 0}
                      >
                        Seçilenlere Uygula ({bulkDiscountModelIds.length})
                      </Button>
                    </div>
                    <Checkbox
                      value="select-all-discount-models"
                      label={
                        bulkDiscountModelIds.length === selectedModels.length
                          ? "Tüm seçimi kaldır"
                          : "Tüm modelleri seç"
                      }
                      isChecked={
                        selectedModels.length > 0 &&
                        bulkDiscountModelIds.length === selectedModels.length
                      }
                      isIndeterminate={
                        bulkDiscountModelIds.length > 0 &&
                        bulkDiscountModelIds.length < selectedModels.length
                      }
                      onChange={({ checked }) =>
                        toggleSelectAllBulkDiscountModels(!!checked)
                      }
                    />
                  </div>
                  <div
                    className="rounded border bg-white overflow-hidden"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <div
                      className="text-muted small fw-semibold border-bottom bg-light"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "32px 1fr 96px",
                        gap: "12px",
                        alignItems: "center",
                        padding: "8px 12px",
                      }}
                    >
                      <span />
                      <span>Model</span>
                      <span>İskonto (%)</span>
                    </div>
                    {selectedModels.map((item, index) => (
                      <div
                        key={item.modelId}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "32px 1fr 96px",
                          gap: "12px",
                          alignItems: "start",
                          padding: "10px 12px",
                          borderBottom:
                            index < selectedModels.length - 1
                              ? "1px solid var(--m-border-color, #e9ecef)"
                              : "none",
                        }}
                      >
                        <div style={{ paddingTop: 2 }}>
                          <Checkbox
                            value={String(item.modelId)}
                            isChecked={bulkDiscountModelIds.includes(
                              item.modelId,
                            )}
                            onChange={({ checked }) =>
                              toggleBulkDiscountModel(item.modelId, !!checked)
                            }
                          />
                        </div>
                        <div className="min-w-0">
                          <div
                            className="fw-semibold"
                            style={{
                              lineHeight: 1.35,
                              wordBreak: "break-word",
                            }}
                          >
                            {item.code} — {item.name}
                          </div>
                          <div className="text-muted small mt-1">
                            <ProductPrice value={item.price || 0} /> ×{" "}
                            {item.count}
                          </div>
                        </div>
                        <Input
                          type="number"
                          placeholder="0"
                          value={String(item.discountPercent ?? 0)}
                          onChange={(v) =>
                            handleLineDiscountChange(
                              item.modelId,
                              v.replace(/[^0-9.,]/g, ""),
                            )
                          }
                          className="discount-input mb-0"
                          min={0}
                          max={100}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="d-flex flex-column gap-2 mt-3 pt-3 border-top">
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted small">Ara toplam</span>
              <span className="fw-semibold">
                <ProductPrice value={calcFinalTotal()} />
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted small">KDV (%20)</span>
              <span className="fw-semibold">
                <ProductPrice value={calcKdvAmount()} />
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted small">Genel toplam</span>
              <span className="fw-semibold">
                <ProductPrice value={calcGrandTotalWithKdv()} />
              </span>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          {hasActiveDiscount && (
            <Button appearance="danger-subtle" onClick={clearAllDiscounts}>
              İskontoları Temizle
            </Button>
          )}
          <Button
            appearance="primary"
            onClick={() => setShowDiscountModal(false)}
          >
            Tamam
          </Button>
        </ModalFooter>
      </Modal>

      <style>{`
        .modern-card { border-radius: 12px; border: 1px solid #e9ecef; overflow: hidden; }
        .section-container {
          margin-bottom: 2rem; padding: 1.75rem;
          background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
          border-radius: 12px; border: 1px solid #e9ecef;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          animation: slideIn 0.4s ease-out;
        }
        .section-header {
          display: flex; align-items: center; gap: 1rem;
          margin-bottom: 1.75rem; padding-bottom: 1rem;
          border-bottom: 2px solid #f1f3f5;
        }
        .section-icon {
          width: 48px; height: 48px; display: flex; align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #00b4f3 0%, #006C80 100%);
          border-radius: 12px; box-shadow: 0 4px 12px rgba(12,132,121,0.2);
          flex-shrink: 0;
        }
        .section-icon svg { width: 24px; height: 24px; color: white; }
        .section-title h5 { font-size: 1.1rem; font-weight: 600; color: #1a202c; margin: 0; }
        .form-control-container {
          background: #fff; border: 1px solid #e2e5e8; border-radius: 8px;
          transition: all 0.2s ease; cursor: pointer;
        }
        .form-control-container .m-select { margin-bottom: 0; }
        .form-control-container .m-select-selected {
          border: none;
          border-radius: 8px;
          background: transparent;
          min-height: 38px;
        }
        .form-control-container:hover { border-color: #00b4f3; }
        .dropdown-text { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .empty-models-container {
          min-height: 200px; border: 2px dashed #dee2e6; border-radius: 16px;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 2rem; background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
        }
        .models-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1.25rem; }
        .model-card { border: 1px solid #e9ecef; border-radius: 12px; background: #fff; overflow: hidden; transition: all 0.3s ease; }
        .model-card:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.12); transform: translateY(-2px); }
        .model-card-header { display: flex; align-items: center; gap: 0.75rem; padding: 1rem; background: linear-gradient(135deg, #f8f9fa, #fff); }
        .model-icon-wrapper { width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #00b4f3, #006C80); border-radius: 10px; flex-shrink: 0; }
        .model-icon { width: 20px; height: 20px; color: white; }
        .model-code { font-size: 14px; font-weight: 700; color: #1a202c; }
        .model-name-text { font-size: 13px; color: #6c757d; }
        .model-card-divider { height: 1px; background: linear-gradient(90deg, transparent, #e9ecef, transparent); }
        .model-card-body { padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
        .model-detail-row { display: flex; justify-content: space-between; align-items: center; }
        .detail-label { font-size: 13px; color: #6c757d; font-weight: 500; }
        .count-container { display: flex; align-items: center; gap: 6px; }
        .count-input { width: 50px !important; text-align: center; }
        .count-input input { text-align: center; padding: 0.25rem 0.5rem; font-weight: 600; }
        .discount-input { max-width: 100px !important; }
        .discount-input input { text-align: right; padding: 0.35rem 0.5rem; }
        .model-price { font-size: 14px; font-weight: 600; color: #00b4f3; }
        .model-termin-inline { display: flex; align-items: center; justify-content: flex-end; gap: 8px; min-width: 0; height: 32px; }
        .model-termin-segment { display: inline-flex; border: 1px solid #d0d5dd; border-radius: 6px; overflow: hidden; flex-shrink: 0; height: 32px; }
        .model-termin-segment button { border: none; background: #fff; padding: 0 10px; font-size: 12px; line-height: 32px; height: 32px; box-sizing: border-box; cursor: pointer; color: #495057; }
        .model-termin-segment button.active { background: #00b4f3; color: #fff; font-weight: 600; }
        .model-termin-value { position: relative; width: 132px; height: 32px; flex-shrink: 0; }
        .model-termin-stock-label,
        .model-termin-date-native { position: absolute; inset: 0; margin: 0; box-sizing: border-box; }
        .model-termin-stock-label { display: flex; align-items: center; justify-content: flex-end; font-size: 12px; font-weight: 700; color: #198754; letter-spacing: 0.02em; pointer-events: none; }
        .model-termin-date-native { width: 100%; height: 32px; padding: 0 6px; font-size: 12px; border: 1px solid #d0d5dd; border-radius: 6px; background: #fff; color: #495057; }
        .model-termin-value .is-hidden { visibility: hidden; pointer-events: none; }
        .model-total-row { display: flex; justify-content: space-between; align-items: center; padding-top: 0.75rem; border-top: 1px solid #e9ecef; }
        .model-total { font-size: 16px; font-weight: 700; color: #00b4f3; }
        .total-amount-container { padding: 1.25rem; background: linear-gradient(135deg, #f8f9fa, #fff); border: 2px solid #00b4f3; border-radius: 12px; }
        .total-amount-label { font-size: 18px; font-weight: 600; color: #1a202c; }
        .total-amount-value { font-size: 24px; font-weight: 700; color: #00b4f3; }
        .model-picker-item { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.25rem; border: 1px solid #e9ecef; border-radius: 10px; margin-bottom: 0.75rem; cursor: pointer; transition: all 0.3s ease; background: #fff; }
        .model-picker-item:hover { border-color: #00b4f3; box-shadow: 0 4px 12px rgba(12,132,121,0.15); transform: translateX(4px); }
        .model-picker-icon { width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #e9ecef, #f8f9fa); border-radius: 8px; }
        .model-picker-icon svg { width: 18px; height: 18px; color: #6c757d; }
        .model-picker-action { opacity: 0; transition: opacity 0.3s ease; }
        .model-picker-item:hover .model-picker-action { opacity: 1; }
        .modal-header-icon { width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #00b4f3, #006C80); border-radius: 10px; color: #fff; font-size: 18px; }
        .form-actions { display: flex; justify-content: flex-end; gap: 1rem; padding: 1.5rem; border-top: 2px solid #f1f3f5; background: linear-gradient(135deg, #fafbfc, #fff); border-radius: 0 0 12px 12px; }
        @keyframes slideIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .w-45 { width: 45% !important; }
        @media (max-width: 768px) { .w-45 { width: 100% !important; } .models-grid { grid-template-columns: 1fr; } .form-actions { flex-direction: column-reverse; } }
      `}</style>
    </AppPage>
  );
};

export default CreateOrder;
